"use client"

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// --- Constants ---
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

// --- SVG Icons ---
const PrinterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9"></polyline>
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
        <rect x="6" y="14" width="12" height="8"></rect>
    </svg>
);

const LoadingSpinner = ({ text = "読み込み中..." }) => (
    <div className="flex items-center text-lg font-semibold text-gray-700">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {text}
    </div>
);

const CreditCardIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
        <line x1="1" y1="10" x2="23" y2="10"></line>
    </svg>
);

function QuotationViewPage() {
    const { token } = useParams();
    const [quotation, setQuotation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // --- 🏢 OEMブランディング用ステート ---
    const [oemBranding, setOemBranding] = useState(null);

    const [isPaying, setIsPaying] = useState(false);

    // 1. データ取得
    useEffect(() => {
        if (!token) {
            setLoading(false);
            setError("見積書トークンが見つかりません。");
            return;
        }

        const fetchQuotation = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_URL}/api/public/quotation/${token}`);
                
                if (!res.ok) {
                    const errData = await res.json().catch(() => ({ error: "サーバーから無効な応答がありました。" }));
                    throw new Error(errData.error || `HTTPエラー: ${res.status}`);
                }

                const data = await res.json();
                
                if (data.success && data.quotation) {
                    const items = data.quotation.items_json;
                    
                    if (!Array.isArray(items)) {
                         throw new Error("見積書データの項目形式が不正です。");
                    }
                    
                    setQuotation({
                        ...data.quotation,
                        items: items,
                        docType: data.docType || 'quotation'
                    });

                    // --- 🏢 APIレスポンスにOEM情報があれば保存 ---
                    if (data.oemBranding) {
                        setOemBranding(data.oemBranding);
                    }

                } else {
                    throw new Error(data.error || "データが見つかりません。");
                }
            } catch (err) {
                console.error("データの取得エラー:", err);
                setError(err.message || "データの取得に失敗しました。");
            } finally {
                setLoading(false);
            }
        };

        fetchQuotation();
    }, [token]);

    // 2. ブラウザタブ（タイトル・ファビコン）の動的書き換え
    useEffect(() => {
        if (!quotation) return;

        const brandName = oemBranding?.display_name || quotation.issuer_company_name;
        const docTitle = getDocumentTitle();
        document.title = `${brandName} | ${docTitle}`;

        // 🚀 修正：ファビコンのパスに /api/oem を追加
        if (oemBranding?.logo_path) {
            const iconUrl = `${API_URL}/api/oem${oemBranding.logo_path}`;
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = iconUrl;
        }
    }, [quotation, oemBranding]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <LoadingSpinner text="ドキュメントを読み込んでいます..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-4">エラー</h2>
                    <p className="text-gray-700">{error}</p>
                    <p className="text-sm text-gray-500 mt-4">このリンクが共有されたものである場合は、送信元にご確認ください。</p>
                </div>
            </div>
        );
    }

    if (!quotation) return null;
    
    const formatCurrency = (amount) => {
        const num = Number(amount);
        return isNaN(num) ? '0' : new Intl.NumberFormat('ja-JP').format(num);
    };

    const handlePayment = async () => {
        try {
            setIsPaying(true);
            const res = await fetch(`${API_URL}/api/public/invoice/${token}/pay`, {
                method: 'POST',
            });
            const data = await res.json();

            if (data.url) {
                window.location.href = data.url;
            } else {
                alert("決済画面の生成に失敗しました: " + (data.error || "不明なエラー"));
            }
        } catch (err) {
            console.error("決済エラー:", err);
            alert("通信エラーが発生しました。");
        } finally {
            setIsPaying(false);
        }
    };

    const getDocumentTitle = () => {
        if (quotation.docType === 'invoice') return '御請求書';
        if (quotation.docType === 'receipt') return '領収書';
        return '御見積書';
    };

    const getNumberLabel = () => {
        if (quotation.docType === 'invoice') return '請求番号';
        if (quotation.docType === 'receipt') return '領収番号';
        return '見積番号';
    };

    return (
        <div className="min-h-screen bg-gray-100 p-3 sm:p-4 md:p-8">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg">
                
                {/* Header and Print Button */}
                <div className="p-5 sm:p-6 md:p-8 border-b flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{getDocumentTitle()}</h1>
                        <p className="text-gray-600 mt-1">発行日: {new Date(quotation.issue_date).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        <p className="text-gray-600 mt-1">{getNumberLabel()}: {quotation.quotation_number}</p>
                    </div>
                    <button
                        onClick={() => window.print()}
                        className="flex w-full items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 print:hidden sm:w-auto"
                    >
                        <PrinterIcon />
                        <span>印刷</span>
                    </button>
                </div>

                <div className="p-5 sm:p-6 md:p-8">
                    {/* Customer and Issuer Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <p className="text-lg font-semibold text-gray-800 border-b pb-2 mb-2">
                                {quotation.customer_company_name || '(宛名)'} 御中
                            </p>
                        </div>
                        
                        <div className="text-sm md:text-right text-gray-700 space-y-1">
                            <p className="font-bold text-base">{quotation.issuer_company_name}</p>
                            <p>{quotation.issuer_address}</p>
                            <p>TEL: {quotation.issuer_phone}</p>
                            <p className="mt-2">担当: {quotation.issuer_representative_name || '(担当者名)'}</p>
                            {quotation.issuer_registration_number && (
                                <p className="mt-1 text-xs text-gray-500">登録番号: {quotation.issuer_registration_number}</p>
                            )}
                        </div>
                    </div>

                    {/* Total Amount */}
                    <div className="relative bg-gray-50 p-5 sm:p-6 rounded-lg text-center mb-8">
                        <p className="text-sm font-semibold text-gray-600 uppercase">合計金額 (税込)</p>
                        <p className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1">
                            ¥ {formatCurrency(quotation.total_amount)}
                        </p>
                       {quotation.docType === 'invoice' && 
                        quotation.stripe_account_id && 
                        quotation.status !== 'paid' && 
                        quotation.allowed_payment_methods?.includes('card') && ( // 👈 ここを追加
                            <div className="mt-6 print:hidden">
                                <button
                                    onClick={handlePayment}
                                    disabled={isPaying}
                                    className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-indigo-600 text-white font-black py-4 px-10 rounded-xl hover:bg-indigo-700 shadow-xl transition-all active:scale-95 disabled:bg-gray-400"
                                >
                                    {isPaying ? "準備中..." : "クレジットカードで支払う"}
                                </button>
                                <p className="text-[10px] text-gray-400 mt-2">※Stripeの安全な決済画面へ移動します</p>
                            </div>
                        )}

                        {/* 入金済みの場合のスタンプ表示 */}
                        {quotation.status === 'paid' && (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-4 border-red-500 text-red-500 font-black text-4xl px-4 py-2 rounded-lg rotate-12 opacity-80 pointer-events-none">
                                入金済み
                            </div>
                        )}
                    </div>

                    {/* Items Table */}
                    <div className="space-y-3 sm:hidden">
                        {quotation.items && quotation.items.map((item, index) => (
                            <div key={item.id || `${index}-mobile`} className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                                <p className="font-medium text-gray-900">{item.name || '(項目名なし)'}</p>
                                <div className="mt-3 grid grid-cols-3 gap-2 text-right text-xs text-gray-600">
                                    <div>
                                        <p className="font-semibold text-gray-400">数量</p>
                                        <p className="mt-1">{formatCurrency(item.quantity)}</p>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-400">単価</p>
                                        <p className="mt-1">{formatCurrency(item.unit_price)}</p>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-400">金額</p>
                                        <p className="mt-1 font-bold text-gray-900">{formatCurrency(item.quantity * item.unit_price)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="hidden overflow-x-auto sm:block">
                        <table className="w-full min-w-[600px] text-left">
                            <thead className="border-b-2 border-gray-200">
                                <tr>
                                    <th className="p-3 text-sm font-semibold text-gray-600 uppercase">品目・摘要</th>
                                    <th className="p-3 text-sm font-semibold text-gray-600 uppercase text-right w-24">数量</th>
                                    <th className="p-3 text-sm font-semibold text-gray-600 uppercase text-right w-32">単価 (円)</th>
                                    <th className="p-3 text-sm font-semibold text-gray-600 uppercase text-right w-32">金額 (円)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {quotation.items && quotation.items.map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td className="p-3 align-top">
                                            <p className="font-medium text-gray-900">{item.name || '(項目名なし)'}</p>
                                        </td>
                                        <td className="p-3 align-top text-right text-gray-700">{formatCurrency(item.quantity)}</td>
                                        <td className="p-3 align-top text-right text-gray-700">{formatCurrency(item.unit_price)}</td>
                                        <td className="p-3 align-top text-right text-gray-700 font-medium">
                                            {formatCurrency(item.quantity * item.unit_price)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Totals Section */}
                    <div className="flex justify-end mt-8">
                        <div className="w-full max-w-xs space-y-2">
                            <div className="flex justify-between text-gray-700">
                                <span>小計</span>
                                <span>¥ {formatCurrency(quotation.subtotal_amount)}</span>
                            </div>
                            <div className="flex justify-between text-gray-700">
                                <span>消費税 (10%)</span>
                                <span>¥ {formatCurrency(quotation.tax_amount)}</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold text-gray-900 border-t pt-2 mt-2">
                                <span>合計金額</span>
                                <span>¥ {formatCurrency(quotation.total_amount)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="mt-8 pt-6 border-t">
                        {quotation.issuer_bank_info && (
                            <div className="mb-4">
                                <h4 className="text-sm font-bold text-gray-800 mb-1">振込先</h4>
                                <p className="text-sm text-gray-600 whitespace-pre-wrap">{quotation.issuer_bank_info}</p>
                            </div>
                        )}
                        {quotation.notes && (
                            <div>
                                <h4 className="text-sm font-bold text-gray-800 mb-1">備考</h4>
                                <p className="text-sm text-gray-600 whitespace-pre-wrap">{quotation.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Footer */}
            <footer className="text-center text-sm text-gray-500 py-6 print:hidden">
                {`© ${new Date().getFullYear()} ${oemBranding?.display_name || quotation.issuer_company_name || "Document Portal"}`}
            </footer>
        </div>
    );
}

export default QuotationViewPage;
